import { useState } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { listOnboarding, getOnboarding, reviewOnboarding } from "../api/onboarding";

// Staff review of public trial applications (gated behind the `kyc` permission,
// same as the KYC page). Read the submission, then approve or reject.

const STATUS_STYLE: Record<string, string> = {
  new: "bg-blue-100 text-blue-700",
  reviewing: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

const FILTERS = ["", "new", "reviewing", "approved", "rejected"];

export function OnboardingReviewPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const [openId, setOpenId] = useState<number | null>(null);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["onboarding", filter],
    queryFn: () => listOnboarding(filter || undefined),
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">Trial Applications</h1>
      <p className="text-sm text-gray-500 mb-4">
        Businesses that applied for a trial through the public application form.
      </p>

      <div className="flex gap-2 mb-3">
        {FILTERS.map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)}
                  className={`px-3 py-1 rounded text-sm ${filter === s ? "bg-brand text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            {s === "" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : !rows || rows.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No applications{filter ? ` with status “${filter}”` : ""} yet.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Business</th>
                <th className="px-4 py-2">Contact</th>
                <th className="px-4 py-2">Traffic</th>
                <th className="px-4 py-2">Submitted</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.application_id} onClick={() => setOpenId(r.application_id)}
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-2 font-medium">{r.legal_business_name}</td>
                  <td className="px-4 py-2">{r.contact_name}<br />
                    <span className="text-gray-400">{r.contact_email}</span></td>
                  <td className="px-4 py-2">{r.traffic_type}</td>
                  <td className="px-4 py-2 text-gray-500">{r.created_at ?? "—"}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${STATUS_STYLE[r.status] ?? "bg-gray-100 text-gray-600"}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId !== null && (
        <DetailModal id={openId} onClose={() => setOpenId(null)}
                     onReviewed={() => { qc.invalidateQueries({ queryKey: ["onboarding"] }); setOpenId(null); }} />
      )}
    </div>
  );
}

function DetailModal({ id, onClose, onReviewed }:
  { id: number; onClose: () => void; onReviewed: () => void }) {
  const { data, isLoading } = useQuery({ queryKey: ["onboarding", "detail", id], queryFn: () => getOnboarding(id) });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function act(status: string) {
    setBusy(true);
    setErr(null);
    try {
      await reviewOnboarding(id, status, note || undefined);
      onReviewed();
    } catch {
      setErr("Couldn't save that — please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-10" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
           onClick={(e) => e.stopPropagation()}>
        {isLoading || !data ? (
          <p className="text-gray-500">Loading…</p>
        ) : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">{data.legal_business_name}</h2>
                {data.dba_name && <p className="text-sm text-gray-500">DBA {data.dba_name}</p>}
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>

            <Group title="Company">
              <Row k="Type" v={data.entity_type} />
              <Row k="EIN / Tax ID" v={data.tax_id_ein} />
              <Row k="Registered" v={data.incorporation_region} />
              <Row k="Website" v={data.website} />
              <Row k="Address" v={[data.address_line1, data.address_line2, `${data.city}, ${data.state_region} ${data.postal_code}`, data.country].filter(Boolean).join(", ")} />
            </Group>

            <Group title="Contact">
              <Row k="Name" v={`${data.contact_name} — ${data.contact_title}`} />
              <Row k="Email" v={data.contact_email} />
              <Row k="Phone" v={data.contact_phone} />
            </Group>

            <Group title="Use case">
              <Row k="Traffic" v={data.traffic_type} />
              <Row k="Volume/day" v={data.expected_daily_volume} />
              <Row k="Regions" v={data.calling_regions} />
              <Row k="Description" v={data.use_case} />
              <Row k="Consent method" v={data.consent_method} />
            </Group>

            <Group title="Acknowledgements">
              <Ack ok={data.attest_consent} label="TCPA consent to call" />
              <Ack ok={data.attest_dnc} label="Honors DNC / opt-outs" />
              <Ack ok={data.attest_accurate} label="Accurate & authorized" />
              <Ack ok={data.agree_terms} label="Agrees to AUP" />
            </Group>

            <div className="mt-5 border-t border-gray-100 pt-4">
              {err && <div className="bg-red-50 text-red-700 text-sm rounded px-3 py-2 mb-3">{err}</div>}
              <label className="block text-sm text-gray-600 mb-1">Reviewer note (optional)</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1024}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3" />
              <div className="flex gap-2 justify-end">
                <button disabled={busy} onClick={() => act("reviewing")}
                        className="px-4 py-2 text-sm rounded bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-60">
                  Mark reviewing
                </button>
                <button disabled={busy} onClick={() => act("rejected")}
                        className="px-4 py-2 text-sm rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-60">
                  Reject
                </button>
                <button disabled={busy} onClick={() => act("approved")}
                        className="px-4 py-2 text-sm rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60">
                  Approve
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string | null | undefined }) {
  if (!v) return null;
  return (
    <div className="flex gap-3 text-sm">
      <span className="text-gray-400 w-32 shrink-0">{k}</span>
      <span className="text-gray-800 whitespace-pre-wrap">{v}</span>
    </div>
  );
}

function Ack({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={ok ? "text-green-600" : "text-red-500"}>{ok ? "✓" : "✗"}</span>
      <span className="text-gray-700">{label}</span>
    </div>
  );
}
